/**
 * React Query hooks for content package operations.
 * Provides declarative data fetching with caching and automatic refetching.
 *
 * All hooks support an optional `sandboxGroupId`. When provided, the request
 * is routed against the group-scoped endpoint
 * (`/sandboxGroups/{id}/contentpackages/...`) and the cache key is namespaced
 * under the group, so default-scope and group-scope data never collide.
 *
 * @module hooks/useContentPackages
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { contentPackageService, ApiException } from '@/services';
import { showErrorToast } from '@/utils/errorToast';
import { sandboxGroupKeys } from './useSandboxGroups';
import type { ContentPackageResponse } from '@/types';

/**
 * Query key factory for content packages.
 * Ensures consistent cache key generation.
 */
export const contentPackageKeys = {
  all: ['contentPackages'] as const,
  lists: () => [...contentPackageKeys.all, 'list'] as const,
  list: () => [...contentPackageKeys.lists()] as const,
  details: () => [...contentPackageKeys.all, 'detail'] as const,
  detail: (id: string) => [...contentPackageKeys.details(), id] as const,
};

/** Resolve the list query key for either default or group scope. */
function listKey(sandboxGroupId?: string) {
  return sandboxGroupId
    ? sandboxGroupKeys.contentPackages(sandboxGroupId)
    : contentPackageKeys.list();
}

/** Resolve the detail query key for either default or group scope. */
function detailKey(id: string, sandboxGroupId?: string) {
  return sandboxGroupId
    ? [...sandboxGroupKeys.contentPackages(sandboxGroupId), 'detail', id]
    : contentPackageKeys.detail(id);
}

/**
 * Hook to fetch all content packages
 *
 * @param sandboxGroupId - Optional sandbox group ID for group-scoped content packages
 * @param enabled - Whether the query should run (defaults to true)
 * @example
 * const { data, isLoading, error } = useContentPackages();
 * const { data } = useContentPackages('group-id');
 * const { data } = useContentPackages('group-id', activeTab === 'contentPackages');
 */
export function useContentPackages(sandboxGroupId?: string, enabled: boolean = true) {
  return useQuery({
    queryKey: listKey(sandboxGroupId),
    queryFn: () => contentPackageService.list(undefined, sandboxGroupId),
    enabled,
  });
}

/**
 * Hook to fetch a single content package by ID
 *
 * @param id - The content package ID
 * @param sandboxGroupId - Optional sandbox group ID for group-scoped content packages
 * @example
 * const { data, isLoading, error } = useContentPackage('my-package-id');
 * const { data } = useContentPackage('my-package-id', 'group-id');
 */
export function useContentPackage(id: string, sandboxGroupId?: string) {
  return useQuery({
    queryKey: detailKey(id, sandboxGroupId),
    queryFn: () => contentPackageService.getById(id, sandboxGroupId),
    enabled: !!id,
  });
}

/**
 * Hook to create a new content package
 *
 * @example
 * const { mutate, isPending } = useCreateContentPackage();
 * mutate({ file, labels }, { onSuccess: () => refetch() });
 * mutate({ file, labels, sandboxGroupId }, { onSuccess: () => refetch() });
 */
export function useCreateContentPackage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      file,
      labels,
      sandboxGroupId,
    }: {
      file: File;
      labels?: Record<string, string>;
      sandboxGroupId?: string;
    }) => contentPackageService.create(file, labels, sandboxGroupId),
    onSuccess: (data: ContentPackageResponse, variables) => {
      // Invalidate the list cache for the scope this CP was created in
      queryClient.invalidateQueries({ queryKey: listKey(variables.sandboxGroupId) });
      // Pre-populate the detail cache
      queryClient.setQueryData(detailKey(data.id, variables.sandboxGroupId), data);
      toast.success(`Content package "${data.id}" created successfully`);
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to create content package: ${error.message}`);
    },
  });
}

/**
 * Hook to delete a content package
 *
 * @example
 * const { mutate, isPending } = useDeleteContentPackage();
 * mutate({ id: 'my-package-id' });
 * mutate({ id: 'my-package-id', sandboxGroupId: 'group-id' });
 */
export function useDeleteContentPackage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, sandboxGroupId }: { id: string; sandboxGroupId?: string }) =>
      contentPackageService.delete(id, sandboxGroupId),
    onSuccess: (_, variables) => {
      // Invalidate the list cache for the scope this CP was deleted from
      queryClient.invalidateQueries({ queryKey: listKey(variables.sandboxGroupId) });
      // Remove from detail cache
      queryClient.removeQueries({ queryKey: detailKey(variables.id, variables.sandboxGroupId) });
      toast.success(`Content package deleted successfully`);
    },
    onError: (error: Error) => {
      // Use detailed message from API if available
      const errorMessage =
        error instanceof ApiException && error.detail ? error.detail : error.message;
      showErrorToast(errorMessage, { duration: 6000 });
    },
  });
}
